import { useState, useRef, useEffect } from 'react';
import { ShippingAddressSectionProps } from '../../types';
import { RadioCard } from './RadioCard';

/**
 * Shipping address selection and input section for checkout.
 * Supports both saved addresses and new address entry.
 */
export function ShippingAddressSection({
  savedShippingLocations,
  useNewAddress,
  selectedShippingLocation,
  newAddress,
  onToggleAddressMode,
  onSelectSavedAddress,
  onNewAddressChange,
  onFetchRates,
  shippingRatesLoading = false,
  showFetchRatesButton = false,
  isGuest = false,
  recipientName,
  recipientPhone,
  onRecipientNameChange,
  onRecipientPhoneChange,
  savePhone = false,
  onSavePhoneChange,
  internationalShipping = false,
}: ShippingAddressSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollIndicators = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    updateScrollIndicators();
  });

  // Guest users should always use new address mode
  const showNewAddressForm = isGuest || useNewAddress;

  const addressSummary = (): string => {
    if (!useNewAddress && selectedShippingLocation) {
      const loc = selectedShippingLocation;
      return [loc.location, loc.address1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');
    }
    if (newAddress?.address1) {
      return [newAddress.address1, newAddress.city, newAddress.state, newAddress.zip].filter(Boolean).join(', ');
    }
    return '';
  };

  // Track which fields have been touched (focused then blurred)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Helper to determine if a field should show error styling
  const hasError = (field: string, value: string | undefined | null) => {
    return touched[field] && !value;
  };

  const isUS = (newAddress?.countrycode ?? 'US') === 'US';
  const isOtherInternational = newAddress?.countrycode === 'ZZ';

  const formatZip = (value: string): string => {
    if (!isUS) return value;
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Input class with conditional error styling
  const inputClass = (field: string, value: string | undefined | null) => {
    const base = 'w-full rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent';
    if (hasError(field, value)) {
      return `${base} border-2 border-red-500 bg-red-50`;
    }
    return `${base} border border-gray-300`;
  };

  const summary = addressSummary();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <button
        type="button"
        onClick={() => setIsCollapsed((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={!isCollapsed}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Shipping Address</h2>
          {isCollapsed && summary && (
            <p className="text-sm text-gray-500 mt-0.5">{summary}</p>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {!isCollapsed && (
        <div className="mt-4">
      {/* Recipient Info - Always visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-1">
            Recipient Name <span className="text-red-500">*</span>
          </label>
          <input
            id="recipientName"
            type="text"
            required
            maxLength={35}
            className={inputClass('recipientName', recipientName)}
            value={recipientName || ''}
            onChange={(e) => onRecipientNameChange(e.target.value)}
            onBlur={() => handleBlur('recipientName')}
            placeholder="Name of person receiving the shipment"
          />
        </div>
        <div>
          <label htmlFor="recipientPhone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
              +1
            </span>
            <input
              id="recipientPhone"
              type="tel"
              required
              maxLength={14}
              className={`${inputClass('recipientPhone', recipientPhone)} rounded-l-none`}
              value={formatPhone(recipientPhone || '')}
              onChange={(e) => onRecipientPhoneChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onBlur={() => handleBlur('recipientPhone')}
              placeholder="(555) 555-5555"
            />
          </div>
          {!isGuest && (
            <label className="flex items-center mt-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={savePhone}
                onChange={(e) => onSavePhoneChange(e.target.checked)}
              />
              <span className="ml-2 text-sm text-gray-600">Save for future checkout</span>
            </label>
          )}
        </div>
      </div>
      {/* Toggle between saved and new address - only show for logged-in users */}
      {!isGuest && (
        <div className="flex space-x-4 mb-4">
          <button
            type="button"
            onClick={() => onToggleAddressMode(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              !useNewAddress
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Saved Addresses
          </button>
          <button
            type="button"
            onClick={() => onToggleAddressMode(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              useNewAddress
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            New Address
          </button>
        </div>
      )}

      {!showNewAddressForm ? (
        <div className="relative overflow-clip">
          {canScrollUp && (
            <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white to-transparent pointer-events-none z-10 flex items-start justify-center pt-1">
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            </div>
          )}
          {canScrollDown && (
            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none z-10 flex items-end justify-center pb-1">
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          )}
          <div
            ref={scrollRef}
            onScroll={updateScrollIndicators}
            className="relative space-y-3 max-h-96 overflow-y-auto pr-1">
          {savedShippingLocations
            .filter((loc) => loc.active)
            .map((location) => (
              <RadioCard
                key={location.id}
                name="shippingLocation"
                value={location.id.toString()}
                checked={selectedShippingLocation?.id === location.id}
                onChange={() => onSelectSavedAddress(location)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-900 flex items-center">
                      {location.location || 'Unnamed Location'}
                      {location.validated && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                          Validated
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      <p>{location.address1}</p>
                      {location.address2 && <p>{location.address2}</p>}
                      <p>
                        {location.city}, {location.state} {location.zip}
                      </p>
                      {location.phone && <p>{location.phone}</p>}
                    </div>
                  </div>
                  {selectedShippingLocation?.id === location.id && (
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </RadioCard>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="countrycode" className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              id="countrycode"
              disabled={!internationalShipping}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              value={newAddress?.countrycode ?? 'US'}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, countrycode: e.target.value, zip: '', countryName: undefined })
              }
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              {internationalShipping && <option value="ZZ">Other (International)</option>}
            </select>
          </div>
          {isOtherInternational && (
            <div className="md:col-span-2">
              <label htmlFor="countryName" className="block text-sm font-medium text-gray-700 mb-1">
                Country Name <span className="text-red-500">*</span>
              </label>
              <input
                id="countryName"
                type="text"
                required
                maxLength={50}
                className={inputClass('countryName', newAddress?.countryName)}
                value={newAddress?.countryName || ''}
                onChange={(e) =>
                  onNewAddressChange({ ...newAddress, countryName: e.target.value })
                }
                onBlur={() => handleBlur('countryName')}
                placeholder="e.g., Germany, Australia, Japan"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <label htmlFor="locationName" className="block text-sm font-medium text-gray-700 mb-1">
              Location Name/ID (optional)
            </label>
            <input
              id="locationName"
              type="text"
              maxLength={50}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newAddress?.location || ''}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, location: e.target.value })
              }
              placeholder="e.g., Main Office, Warehouse 2"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="address1" className="block text-sm font-medium text-gray-700 mb-1">
              Street Address <span className="text-red-500">*</span>
            </label>
            <input
              id="address1"
              type="text"
              required
              className={inputClass('address1', newAddress?.address1)}
              value={newAddress?.address1 || ''}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, address1: e.target.value })
              }
              onBlur={() => handleBlur('address1')}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">
              Apartment, suite, etc. (optional)
            </label>
            <input
              id="address2"
              type="text"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newAddress?.address2 || ''}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, address2: e.target.value })
              }
            />
          </div>
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              id="city"
              type="text"
              required
              className={inputClass('city', newAddress?.city)}
              value={newAddress?.city || ''}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, city: e.target.value })
              }
              onBlur={() => handleBlur('city')}
            />
          </div>
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              State <span className="text-red-500">*</span>
            </label>
            <input
              id="state"
              type="text"
              required
              className={inputClass('state', newAddress?.state)}
              value={newAddress?.state || ''}
              onChange={(e) =>
                onNewAddressChange({ ...newAddress, state: e.target.value })
              }
              onBlur={() => handleBlur('state')}
            />
          </div>
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
              {isUS ? 'ZIP Code' : 'Postal Code'}{!isOtherInternational && <span className="text-red-500"> *</span>}
            </label>
            <input
              id="zip"
              type="text"
              required={!isOtherInternational}
              maxLength={10}
              className={isOtherInternational ? 'w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent' : inputClass('zip', newAddress?.zip)}
              value={formatZip(newAddress?.zip || '')}
              onChange={(e) =>
                onNewAddressChange({
                  ...newAddress,
                  zip: isUS ? e.target.value.replace(/\D/g, '').slice(0, 9) : e.target.value.slice(0, 10),
                })
              }
              onBlur={() => handleBlur('zip')}
              placeholder={isOtherInternational ? 'Optional' : undefined}
            />
          </div>
          {showFetchRatesButton && (
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={onFetchRates}
                disabled={
                  shippingRatesLoading ||
                  !newAddress?.address1 ||
                  !newAddress?.city ||
                  !newAddress?.state ||
                  (!isOtherInternational && !newAddress?.zip) ||
                  (isOtherInternational && !newAddress?.countryName)
                }
                className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {shippingRatesLoading ? 'Loading Rates...' : 'Update Shipping Rates'}
              </button>
            </div>
          )}
        </div>
        )}
      </div>
      )}
    </div>
  );
}
